import { Component, computed, effect, inject, signal, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { NursingCareDiagnosisUpsertDialogStore } from './nursing-care-diagnosis-upsert-dialog.store';
import { Validators } from '@angular/forms';
import {
    VoucherNursingCareDiagnosisCreateInput,
    VoucherNursingCareDiagnosisGridItemObject,
    VoucherNursingCareDiagnosisGridObject,
    VoucherNursingCareDiagnosisUpdateInput,
} from '@it2go/types';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { TranslatePipe } from '@ngx-translate/core';
import { ChipModule } from '@openng/optimus-ui/chip';
import {
    MultiSelectInputComponent,
} from '../../../../../../components/kit/input/multi-select-input/multi-select-input.component';
import { fb } from '../../../../../shared/shared.global-service';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { contentGlobalSuccess } from '../../../../content.error';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { InfiniteScrollStore } from '../../../../../shared/store/common/infinite-scroll.store';
import { diagnosesFrag } from './nursing-care-diagnosis-upsert-dialog.query';
import { changeSignal } from '../../../../../../utils/form.utils';

@Component({
    selector: 'app-nursing-care-diagnosis-upsert-dialog',
    templateUrl: './nursing-care-diagnosis-upsert-dialog.component.html',
    styleUrl: './nursing-care-diagnosis-upsert-dialog.component.scss',
    providers: [NursingCareDiagnosisUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AccordionModule,
        TranslatePipe,
        ChipModule,
        MultiSelectInputComponent,
        SelectInputComponent,
        InputWysiwygComponent,
    ],
})
export class NursingCareDiagnosisUpsertDialogComponent extends BaseDialogComponent {

    public readonly store = inject(NursingCareDiagnosisUpsertDialogStore);

    public readonly wysiwygs = viewChildren<InputWysiwygComponent>(InputWysiwygComponent);

    protected prefix = 'folder.voucher.nursingCarePlan.createDiagnosis.';

    protected form = fb.group({
        diagnosisCodeId: fb.control<number | null>(null, [Validators.required]),
        symptoms: fb.control<number[]>([]),
        symptomsComment: fb.control<string>(''),
        factors: fb.control<number[]>([]),
        factorsComment: fb.control<string>(''),
        riskFactors: fb.control<number[]>([]),
        riskFactorsComment: fb.control<string>(''),
        goals: fb.control<number[]>([]),
        goalsComment: fb.control<string>(''),
        interventions: fb.control<number[]>([]),
        interventionsComment: fb.control<string>(''),
    });

    protected voucherId!: string;
    protected diagnosis!: VoucherNursingCareDiagnosisGridObject | null;

    protected selectedCode = signal<VoucherNursingCareDiagnosisGridItemObject | null>(null);

    protected diagnosisScroll = computed(() =>
        new InfiniteScrollStore(
            this.store.api.getDiagnosisCodes,
            FilterBuilder.filter({
                serviceId: this.store.serviceId(),
                active: true,
                limit: 100,
                sort: { column: 'code' },
            }),
            true,
            diagnosesFrag,
            undefined,
            true,
        ),
    );

    private diagnosisCodeChanged = changeSignal(this.form.controls.diagnosisCodeId);

    constructor() {
        super();

        this.voucherId = this.dialogConfig.data.voucherId;
        this.diagnosis = this.dialogConfig.data.diagnosis;

        if (this.diagnosis) {
            this.form.patchValue({
                ...this.diagnosis,
                symptoms: (this.diagnosis.symptoms || []).map((it: any) => it.id),
                factors: (this.diagnosis.factors || []).map((it: any) => it.id),
                riskFactors: (this.diagnosis.riskFactors || []).map((it: any) => it.id),
                goals: (this.diagnosis.goals || []).map((it: any) => it.id),
                interventions: (this.diagnosis.interventions || []).map((it: any) => it.id),
            });
        }

        effect(async () => {
            const code = this.diagnosisCodeChanged();
            if (!code) return;
            const res = await this.store.api.getDiagnosisCodes(FilterBuilder.filterById(code));
            this.selectedCode.set(res?.items?.[0] || null);
        });
    }

    public override async save() {
        if (isInvalid(this.form)) return;

        const data = {
            ...this.form.value,
            voucherNursingCareId: this.diagnosis ? undefined : this.voucherId,
            id: this.diagnosis?.id ?? undefined,
        } as unknown as VoucherNursingCareDiagnosisCreateInput & VoucherNursingCareDiagnosisUpdateInput;

        if (this.diagnosis) {
            await this.store.api.updateNursingCareDiagnosis(data);
            this.wysiwygs()?.forEach((it) => it.resetBackupValues());

            contentGlobalSuccess.next('common.updated');
        } else {
            await this.store.api.createNursingCareDiagnosis(data);
            this.wysiwygs()?.forEach((it) => it.resetBackupValues());

            contentGlobalSuccess.next('common.created');
        }

        this.dialogRef.close();
    }
}
